import React, { useState } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const CompanyDriverApplication = () => {
  return <JobApplicationForm type="company_driver" title="Company Driver Application" />;
};

export const OwnerOperatorApplication = () => {
  return <JobApplicationForm type="owner_operator" title="Owner Operator Application" />;
};

const JobApplicationForm = ({ type, title }) => {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    cdl_number: '',
    cdl_state: '',
    years_experience: '',
    previous_employer: '',
    truck_info: '',
    additional_info: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      await axios.post(`${API}/applications`, {
        ...formData,
        application_type: type,
        years_experience: formData.years_experience ? parseInt(formData.years_experience) : null
      });
      setSuccess(true);
      setFormData({
        full_name: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        zip_code: '',
        cdl_number: '',
        cdl_state: '',
        years_experience: '',
        previous_employer: '',
        truck_info: '',
        additional_info: ''
      });
      window.scrollTo(0, 0);
    } catch (err) {
      setError('Failed to submit application. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="py-16" data-testid="application-page">
      <div className="max-w-4xl mx-auto px-4">
        <h1 className="text-4xl font-bold mb-8 text-center">{title}</h1>
        
        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded mb-6" data-testid="application-success">
            Thank you for your application! We'll review it and get back to you soon.
          </div>
        )}
        
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6" data-testid="application-error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="bg-white p-8 rounded-lg shadow-lg" data-testid="application-form">
          <h2 className="text-2xl font-bold mb-6">Personal Information</h2>
          
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium mb-1">Full Name *</label>
              <input
                type="text"
                name="full_name"
                value={formData.full_name}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                data-testid="app-full-name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Email *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                data-testid="app-email"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Phone *</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                data-testid="app-phone"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Address *</label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                data-testid="app-address"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">City *</label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                data-testid="app-city"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">State *</label>
              <input
                type="text"
                name="state"
                value={formData.state}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                data-testid="app-state"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">ZIP Code *</label>
              <input
                type="text"
                name="zip_code"
                value={formData.zip_code}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                data-testid="app-zip"
              />
            </div>
          </div>

          <h2 className="text-2xl font-bold mb-6">Driving Information</h2>
          
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium mb-1">CDL Number *</label>
              <input
                type="text"
                name="cdl_number"
                value={formData.cdl_number}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                data-testid="app-cdl-number"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">CDL State *</label>
              <input
                type="text"
                name="cdl_state"
                value={formData.cdl_state}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                data-testid="app-cdl-state"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Years of Experience *</label>
              <input
                type="number"
                name="years_experience"
                value={formData.years_experience}
                onChange={handleChange}
                required
                min="0"
                className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                data-testid="app-experience"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Previous Employer</label>
              <input
                type="text"
                name="previous_employer"
                value={formData.previous_employer}
                onChange={handleChange}
                className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                data-testid="app-prev-employer"
              />
            </div>
          </div>

          {type === 'owner_operator' && (
            <>
              <h2 className="text-2xl font-bold mb-6">Truck Information</h2>
              <div className="mb-6">
                <label className="block text-sm font-medium mb-1">Truck Details (Make, Model, Year)</label>
                <textarea
                  name="truck_info"
                  value={formData.truck_info}
                  onChange={handleChange}
                  rows="3"
                  className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  data-testid="app-truck-info"
                />
              </div>
            </>
          )}

          <div className="mb-6">
            <label className="block text-sm font-medium mb-1">Additional Information</label>
            <textarea
              name="additional_info"
              value={formData.additional_info}
              onChange={handleChange}
              rows="4"
              placeholder="Please provide any additional information that would help us evaluate your application..."
              className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-600 focus:border-transparent"
              data-testid="app-additional-info"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white py-3 rounded-md hover:bg-blue-700 transition font-medium disabled:bg-gray-400"
            data-testid="app-submit-btn"
          >
            {loading ? 'Submitting...' : 'Submit Application'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default JobApplicationForm;
